export type DrEditionApiError = {
	status: number | string;
	title: string;
	details: string;
};

export type BoomError = {
	statusCode: number;
	error: string;
	message: string;
};

export type ApiResponse<T> = ApiOKResponse<T> | ApiErrorResponse;

export type ApiOKResponse<T> = {
	status: 200;
	body: T;
};

export type ApiErrorResponse = {
	status: 400 | 401 | 404;
	body: DrEditionApiError;
};

export function formatErrorResponse(body: unknown): DrEditionApiError {
	// Runtime type checking since response.json() returns unknown
	if (body && typeof body === 'object') {
		// Check if it's a BoomError format
		if ('statusCode' in body && 'error' in body && 'message' in body) {
			const boomError = body as BoomError;
			return {
				status: boomError.statusCode,
				title: boomError.error,
				details: boomError.message
			};
		}
		// Check if it's already in DrEditionApiError format
		if ('status' in body && 'title' in body && 'details' in body) {
			return body as DrEditionApiError;
		}
	}

	// Fallback for unexpected error formats
	return {
		status: 500,
		title: 'Unknown Error',
		details: 'An unexpected error occurred'
	};
}

/**
 * Generic helper to handle RPC responses and convert them to ApiResponse format
 */
export async function handleRpcResponse<T>(
	responsePromise: Promise<Response>
): Promise<ApiOKResponse<T> | ApiErrorResponse> {
	const response = await responsePromise;

	if (response.status === 200) {
		return {
			status: 200,
			body: (await response.json()) as T
		};
	}

	return {
		status: response.status as 400 | 401 | 404,
		body: formatErrorResponse(await response.json())
	};
}
